import React from 'react';
import { createRoot } from 'react-dom/client';
import { ApolloProvider } from '@apollo/client';
import { MuiThemeProvider as MuiNextThemeProvider } from '@material-ui/core/styles';
import { StyledEngineProvider, ThemeProvider } from '@mui/material/styles';
import { LicenseInfo } from '@mui/x-license-pro';
import { StytchProvider } from '@stytch/react';
import { StytchUIClient } from '@stytch/vanilla-js';
import { cloverThemeVars } from 'fe-design-base/styles/clover/vars';
import designBaseTheme from 'fe-design-base/styles/theme';
import MuiThemeProvider from 'material-ui/styles/MuiThemeProvider';
import muiTheme from 'theme/mui';
import muiNextTheme from 'theme/muiNext';

import LeftPanelPayrollEoy from 'features/authentication/SignIn/LeftPanel/LeftPanelPayrollEoy';
import LeftPanelReferralBanner from 'features/authentication/SignIn/LeftPanel/LeftPanelReferralBanner';
import LeftNavigation from 'features/LeftNavigationView';

import Loader from 'components/Loader';

import getIsEmbedded from 'util/embedded';

import { createApolloClient } from './apolloClient';
import App from './App';
import CommandBar from './CommandBar';
import { MUI_PRO_X_KEY } from './constants';
import Nav from './Nav';

const RootModal = React.lazy(() =>
  import(
    /* webpackPreload: true, webpackChunkName: "MainComp-RootModal" */ '../features/modals/RootModal'
  )
);
const NoAccessManagerLogView = React.lazy(() =>
  import(
    /* webpackPreload: true, webpackChunkName: "MainComp-NoAccessManagerLogView" */ '../features/productGrowth/NoAccessManagerLogView'
  )
);

const EnforcementTooltipWrapperBackbone = React.lazy(() =>
  import('./EnforcementTooltipWrapperBackbone')
);

const ComponentCallbacks = React.lazy(() => import('./ComponentCallbacks'));

export default class RootMounter {
  constructor(store, stytchPublicToken) {
    const stytchOptions = {
      endpointOptions: {
        dfppaDomain: 'telemetry.joinhomebase.com',
      },
    };

    LicenseInfo.setLicenseKey(MUI_PRO_X_KEY);

    this.store = store;
    this.stytch = new StytchUIClient(stytchPublicToken, stytchOptions);
    this.apolloClient = createApolloClient();
  }

  mountAll = () => {
    this.renderRootIfPresent(App, 'react-app-root');
    if (module.hot) {
      module.hot.accept('./App', () =>
        this.renderRootIfPresent(App, 'react-app-root')
      );
    }
    this.renderRootIfPresent(Nav, 'react-nav-root');
    this.renderRootIfPresent(LeftNavigation, 'react-left-nav-root');
    this.renderRootIfPresent(RootModal, 'react-modal-root');
    this.renderRootIfPresent(
      ComponentCallbacks,
      'react-component-callback-root'
    );
    this.renderRootIfPresent(CommandBar, 'react-command-bar-root');
    this.renderRootIfPresent(
      LeftPanelReferralBanner,
      'left-panael-referral-banner'
    );
    this.renderRootIfPresent(LeftPanelPayrollEoy, 'left-panel-payroll-eoy');
  };

  renderRootIfPresent = (Component, rootId, props = {}) => {
    if (document.getElementById(rootId)) {
      this.renderRoot(Component, rootId, props);
    }
  };

  mountApp = () => {
    this.renderRootIfPresent(App, 'react-app-root');
  };

  mountHasNoManagerLogAccessView = () => {
    this.renderRootIfPresent(
      NoAccessManagerLogView,
      'react-no-manager-log-access-root'
    );
  };

  mountEnforcementTooltipWrapper = (elementId, props) => {
    this.renderRootIfPresent(
      EnforcementTooltipWrapperBackbone,
      elementId,
      props
    );
  };

  applyEmbeddedStyles = () => {
    const cssRoot = document.querySelector(':root');
    for (const [cssVarKey, cssVarValue] of Object.entries(cloverThemeVars)) {
      cssRoot?.style.setProperty(cssVarKey, cssVarValue);
    }

    const bodyContainer = document.querySelector('.body-container');
    if (bodyContainer) {
      bodyContainer.style.backgroundColor = cloverThemeVars['--mono0'];
    }
  };

  renderRoot = (Component, rootId, props) => {
    const container = document.getElementById(rootId);
    const root = createRoot(container);
    const isEmbedded = getIsEmbedded();

    if (isEmbedded) {
      this.applyEmbeddedStyles();
    }

    root.render(
      <React.Suspense fallback={<Loader isLoading centered />}>
        <StytchProvider stytch={this.stytch}>
          <ApolloProvider client={this.apolloClient}>
            <MuiNextThemeProvider theme={muiNextTheme}>
              <MuiThemeProvider muiTheme={muiTheme}>
                <ThemeProvider theme={designBaseTheme}>
                  <StyledEngineProvider>
                    <Component store={this.store} {...props} />
                  </StyledEngineProvider>
                </ThemeProvider>
              </MuiThemeProvider>
            </MuiNextThemeProvider>
          </ApolloProvider>
        </StytchProvider>
      </React.Suspense>
    );
  };
}
